<template>
	<section>
		<loading-indicator v-if="isLoading" loading-text="Loading User Account..." class="ml-3" />
		<div v-else-if="userAccount">
			<v-layout row wrap>
				<v-flex lg6 md12>
					<v-list two-line class="flexible-height">
						<v-list-tile inactive>
							<v-list-tile-action>
								<v-icon>mdi-email-outline</v-icon>
							</v-list-tile-action>

							<v-list-tile-content>
								<v-list-tile-title>{{ userAccount.emailAddress }}</v-list-tile-title>
								<v-list-tile-sub-title>Email Address</v-list-tile-sub-title>
								<v-list-tile-sub-title>
									<div v-if="isAdmin"><a href="#" @click.prevent.stop="showConfirmRemoveLoginDialog = true" class="caption red--text">Revoke Access</a></div>

									<a v-if="userAccount.isSelf || !userAccount.hasAccessToMultipleClients" href="#" class="caption" @click.prevent.stop="onChangePasswordClick">Reset Password</a>
									<span v-else class="caption warning--text"
										><v-icon small class="mr-1" color="warning">mdi-link-variant</v-icon>This login is linked with multiple accounts. Please contact a system administrator if you want to reset the password.</span
									>
								</v-list-tile-sub-title>
							</v-list-tile-content>

							<v-list-tile-action-text> </v-list-tile-action-text>
						</v-list-tile>
					</v-list>
				</v-flex>
				<v-flex lg6 md12>
					<div v-if="isAdmin || isSelf">
						<v-list two-line class="flexible-height">
							<v-list-tile inactive>
								<v-list-tile-action>
									<v-icon :color="twoFactorAuthIconColour">{{ twoFactorAuthIcon }}</v-icon>
								</v-list-tile-action>

								<v-list-tile-content>
									<v-list-tile-title
										>{{ twoFactorAuthEnabledText }}
										<v-tooltip right max-width="350" content-class="text-xs-left">
											<v-icon small slot="activator" color="info">mdi-help-circle</v-icon>
											<span>Two-factor authentication is a security process that requires two forms of user ID to log in to Your HR System.</span>
										</v-tooltip>
									</v-list-tile-title>
									<v-list-tile-sub-title v-if="isTwoFactorAuthEnabled">
										<span v-if="isSettingMFA" class="caption">Please wait...</span>
										<span v-else>
											<span v-if="canDisableTwoFactorAuth"><a href="#" class="caption" @click="showConfirmDisableMFADialog = true">Disable 2FA</a> | </span
											><a href="#" class="caption" @click="showConfirmResetMFADialog = true">Reset 2FA</a>
										</span>
										<div v-if="userAccount.hasAccessToMultipleClients" class="caption warning--text">
											Two-factor authentication is required by all users with access to multiple accounts.
										</div>
									</v-list-tile-sub-title>
									<v-list-tile-sub-title v-else>
										<a v-if="!isSettingMFA" href="#" class="caption" @click="showConfirmEnableMFADialog = true">Enable 2FA</a>
										<span v-else class="caption">Please wait...</span>
									</v-list-tile-sub-title>
								</v-list-tile-content>
							</v-list-tile>
						</v-list>
					</div>
				</v-flex>
			</v-layout>
		</div>
		<div v-else class="text-xs-left">
			<v-subheader>No logins exist for this employee</v-subheader>
			<div>
				<v-btn class="primary" @click="onCreateLoginClick"> <v-icon left>add</v-icon>Create Login </v-btn>
			</div>
		</div>

		<employee-change-password
			v-if="changePasswordEditorDialog"
			:dialog="changePasswordEditorDialog"
			@save="updatePassword"
			:isSaving="isUpdatingPassword"
			:showNotifyEmployeeOption="!userAccount.isSelf"
			@closeEditor="changePasswordEditorDialog = false"
		>
		</employee-change-password>

		<employee-login-editor
			v-if="createLoginDialog"
			title="Create Login"
			:dialog="createLoginDialog"
			:email="employeeEmail"
			:isAdmin="loggedInEmployee.isAdmin"
			:isSaving="isCreatingLogin"
			@save="createLogin"
			@closeEditor="createLoginDialog = false"
		/>

		<confirmation-dialog v-if="showConfirmEnableMFADialog" title="Confirm Enable" :dialog="showConfirmEnableMFADialog" :isConfirming="isSettingMFA" @onConfirm="enableMFA" @onCancel="showConfirmEnableMFADialog = false">
			<div class="pt-3 pb-2">
				Please confirm that you want to enable two-factor authentication for this user.
				<v-alert :value="true" class="text-xs-left mt-3" color="info" icon="info" outline>Note: If the user has not yet provided their phone number, they will be prompted to during the first sign-in.</v-alert>
			</div>
		</confirmation-dialog>

		<confirmation-dialog v-if="showConfirmDisableMFADialog" title="Confirm Disable" :dialog="showConfirmDisableMFADialog" :isConfirming="isSettingMFA" @onConfirm="disableMFA" @onCancel="showConfirmDisableMFADialog = false">
			<div class="pt-3 pb-2">Please confirm that you want to disable two-factor authentication for this user.</div>
		</confirmation-dialog>

		<confirmation-dialog v-if="showConfirmResetMFADialog" title="Confirm Reset" :dialog="showConfirmResetMFADialog" :isConfirming="isSettingMFA" @onConfirm="resetMFASettings" @onCancel="showConfirmResetMFADialog = false">
			<div class="pt-3 pb-2">Please confirm that you want to reset the two-factor authentication settings for this user.</div>
			<v-alert :value="true" class="text-xs-left mt-3" color="info" icon="info" outline>This will allow the user to re-enter their phone number the next time they sign-in.</v-alert>
		</confirmation-dialog>

		<confirmation-dialog v-if="showConfirmRemoveLoginDialog" title="Confirm Revoke Access" :dialog="showConfirmRemoveLoginDialog" :isConfirming="isRemovingLogin" @onConfirm="confirmRemoveLogin" @onCancel="showConfirmRemoveLoginDialog = false">
			<div class="pt-3 pb-2">Please confirm that you want to revoke access using this login?</div>
		</confirmation-dialog>
	</section>
</template>

<script>
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import toast from '@/services/toasts/index.js';
	import EmployeeChangePassword from '@/components/employee/EmployeeChangePassword';
	import EmployeeLoginEditor from '@/components/employee/EmployeeLoginEditor';
	import MFARequirementLevel from '@/enums/mfaRequirementLevel';
	import { mapGetters } from 'vuex';

	export default {
		components: {
			LoadingIndicator,
			EmployeeChangePassword,
			EmployeeLoginEditor
		},
		props: {
			employeeId: {
				type: String,
				required: true
			},
			employeeEmail: {
				type: String,
				required: false
			}
		},
		data() {
			return {
				userAccount: null,
				isLoading: false,
				changePasswordEditorDialog: false,
				isSettingMFA: false,
				showConfirmEnableMFADialog: false,
				showConfirmDisableMFADialog: false,
				showConfirmResetMFADialog: false,
				showConfirmRemoveLoginDialog: false,
				isUpdatingPassword: false,
				createLoginDialog: false,
				isCreatingLogin: false,
				isRemovingLogin: false
			};
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee'
			}),
			isTwoFactorAuthEnabled() {
				if (!this.userAccount) return false;
				return this.userAccount.isMFAEnabled;
			},
			twoFactorAuthEnabledText() {
				return this.isTwoFactorAuthEnabled ? '2FA Enabled' : '2FA Disabled';
			},
			canDisableTwoFactorAuth() {
				return this.userAccount.mfaRequirementLevel === MFARequirementLevel.Individual;
			},
			twoFactorAuthIcon() {
				return this.isTwoFactorAuthEnabled ? 'mdi-lock-outline' : 'mdi-lock-open-outline';
			},
			twoFactorAuthIconColour() {
				return this.isTwoFactorAuthEnabled ? 'success' : 'warning';
			},
			isAdmin() {
				return this.loggedInEmployee.isAdmin;
			},
			isSelf() {
				if (!this.userAccount) return false;
				return this.loggedInEmployee.userId === this.userAccount.userId;
			}
		},
		async created() {
			await this.loadUserAccount();
		},
		methods: {
			async loadUserAccount() {
				this.isLoading = true;
				const result = await this.$store.dispatch('employees/getUserAccount', this.employeeId);
				if (result.isSuccess) {
					this.userAccount = result.data;
				}
				this.isLoading = false;
			},
			onChangePasswordClick() {
				this.changePasswordEditorDialog = !this.changePasswordEditorDialog;
			},
			updatePassword(data) {
				this.isUpdatingPassword = true;

				var obj = {
					userId: this.userAccount.userId,
					password: data.password,
					employeeId: this.employeeId,
					notifyEmployee: data.notifyEmployee
				};
				this.$store.dispatch('employees/updatePassword', obj).then(
					response => {
						toast.success('Password Updated');
						this.changePasswordEditorDialog = false;
						this.isUpdatingPassword = false;
					},
					error => {
						this.isUpdatingPassword = false;
						toast.error(`Password Update Failed - ${error.response.data}`);
					}
				);
			},
			async enableMFA() {
				this.isSettingMFA = true;
				const result = await this.$store.dispatch('employees/setMFA', { employeeId: this.employeeId, enabled: true });
				if (result.isSuccess) {
					this.userAccount.isMFAEnabled = true;
					this.showConfirmEnableMFADialog = false;
					toast.success('Two-factor authentication has been enabled');
				}
				this.isSettingMFA = false;
			},
			async disableMFA() {
				this.isSettingMFA = true;
				const result = await this.$store.dispatch('employees/setMFA', { employeeId: this.employeeId, enabled: false });
				if (result.isSuccess) {
					this.userAccount.isMFAEnabled = false;
					this.showConfirmDisableMFADialog = false;
					toast.success('Two-factor authentication has been disabled');
				}
				this.isSettingMFA = false;
			},
			async resetMFASettings() {
				this.isSettingMFA = true;
				const result = await this.$store.dispatch('employees/resetMFASettings', this.employeeId);
				if (result.isSuccess) {
					this.showConfirmResetMFADialog = false;
					toast.success('Two-factor authentication settings have been reset');
				}
				this.isSettingMFA = false;
			},
			async createLogin(data) {
				this.isCreatingLogin = true;

				var payload = {
					employeeId: this.employeeId,
					password: data.password,
					email: data.email,
					roles: data.roles,
					notifyEmployee: data.notifyEmployee
				};

				try {
					await this.$store.dispatch('employees/createLogin', payload);

					await this.loadUserAccount();

					toast.success('Login Created');

					this.createLoginDialog = false;

					this.$emit('onUserAccountUpdated', this.userAccount);
				} catch (error) {
					toast.error(`Login Creation Failed - ${error.response.data}`);
				} finally {
					this.isCreatingLogin = false;
				}
			},
			onCreateLoginClick() {
				this.createLoginDialog = !this.createLoginDialog;
			},
			async confirmRemoveLogin() {
				this.isRemovingLogin = true;

				var obj = {
					employeeId: this.employeeId,
					userId: this.userAccount.userId
				};

				try {
					await this.$store.dispatch('employees/removeLogin', obj);

					await this.loadUserAccount();

					toast.success('Access has been revoked');

					this.showConfirmRemoveLoginDialog = false;

					this.$emit('onUserAccountUpdated', this.userAccount);
				} catch (error) {
					toast.error(`Failed to revoke access - ${error.response.data}`);
				}
				finally {
					this.isRemovingLogin = false;
				}
			}
		}
	};
</script>
